import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { LayoutDashboard, Users, Sparkles, Handshake, Cpu, ScrollText, ArrowLeft, CreditCard, Gift, SlidersHorizontal, Loader2, Radar, Activity, ShieldAlert, Mail, LinkIcon, Percent, ShieldCheck, Wallet, Menu } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/subscriptions", label: "Subscriptions", icon: CreditCard },
  { to: "/admin/promotions", label: "Promotions", icon: Percent },
  { to: "/admin/affiliates", label: "Affiliates", icon: Handshake },
  { to: "/admin/payout-waitlist", label: "Payout Waitlist", icon: Wallet },
  { to: "/admin/referrals", label: "Referrals", icon: Gift },
  { to: "/admin/leads", label: "CV Analysis Leads", icon: Sparkles },
  { to: "/admin/matcher", label: "Job Matcher", icon: Radar },
  { to: "/admin/sponsor-reviews", label: "Sponsor Register", icon: ShieldCheck },
  { to: "/admin/llm", label: "LLM Settings", icon: Cpu },
  { to: "/admin/llm-usage", label: "LLM Usage", icon: Activity },
  { to: "/admin/email", label: "Email", icon: Mail },
  { to: "/admin/gdpr", label: "GDPR / Data", icon: ShieldAlert },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText },
  { to: "/admin/links", label: "Platform Links", icon: LinkIcon },
  { to: "/admin/settings", label: "Settings", icon: SlidersHorizontal },
];

const testId = (label) => `admin-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`;

export default function AdminLayout() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [ai, setAi] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { if (user?.is_admin) api.get("/admin/action-items").then((r) => setAi(r.data)).catch(() => {}); }, [user]);
  if (loading || user === null) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!user?.is_admin) return <Navigate to="/profile" replace />;
  const badges = {
    "/admin/subscriptions": ai?.trials_expiring_3d || 0,
    "/admin/affiliates": (ai?.pending_affiliate_apps || 0) + (ai?.affiliates_no_stripe || 0),
    "/admin/sponsor-reviews": ai?.pending_sponsor_reviews || 0,
  };
  return (
    <div className="min-h-[calc(100vh-4rem)] -mx-4 -my-6 bg-zinc-50" data-testid="admin-layout">
      <div className="flex items-center gap-3 px-4 py-3 bg-[#1a3060] text-white">
        <button onClick={() => setOpen(true)} data-testid="admin-menu-button" aria-label="Open admin menu"
          className="flex items-center justify-center h-10 w-10 rounded-lg bg-white/10 hover:bg-white/20 active:scale-95 transition-transform">
          <Menu className="h-5 w-5" strokeWidth={2} />
        </button>
        <div>
          <p className="font-heading font-bold tracking-tight leading-tight">Jobsmith Admin</p>
          <p className="text-[11px] text-white/50 leading-tight">Operator console</p>
        </div>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-[264px] p-0 flex flex-col bg-[#1a3060] text-white/90 border-r-0" data-testid="admin-drawer">
          <SheetHeader className="px-4 py-4 border-b border-white/10 text-left">
            <SheetTitle className="text-white">Jobsmith Admin</SheetTitle>
            <SheetDescription className="text-white/50 text-[11px]">Operator console</SheetDescription>
          </SheetHeader>
          <nav className="flex-1 overflow-y-auto p-2 space-y-1">
            {NAV.map((n) => {
              const Icon = n.icon;
              const count = badges[n.to] || 0;
              return (
                <NavLink key={n.to} to={n.to} end={n.end} data-testid={testId(n.label)} onClick={() => setOpen(false)}
                  className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "bg-[#E8A93C] text-[#1a3060] font-semibold" : "hover:bg-white/10"}`}>
                  <Icon className="h-4 w-4" strokeWidth={1.75} /> <span className="flex-1">{n.label}</span>
                  {count > 0 && <span className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-[#E8A93C] text-[#1a3060] text-[11px] font-bold" data-testid={`admin-nav-badge-${testId(n.label).replace("admin-nav-", "")}`}>{count}</span>}
                </NavLink>
              );
            })}
          </nav>
          <button onClick={() => { setOpen(false); navigate("/profile"); }} className="m-2 flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/60 hover:bg-white/10" data-testid="admin-exit">
            <ArrowLeft className="h-4 w-4" /> Back to app
          </button>
        </SheetContent>
      </Sheet>

      <main className="p-6 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
